import React, { useState, useEffect } from 'react';
import { supabase } from '../global/supabase';
import HamburgerMenu from '../global/menu';
import { LOCATIONS, BRANDING } from '../config';
import '../global/styles.css';

function ProviderUsageReport({ userInfo, isAdmin, onLogout, onNavigate }) {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [reportData, setReportData] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showProviderChart, setShowProviderChart] = useState(false);
  const [showLocationChart, setShowLocationChart] = useState(false);
  const [chartData, setChartData] = useState({ providers: {}, locations: {} });

  // Get location tables from config
  const locationTables = LOCATIONS.map(loc => loc.tableName);

  // Load all users for the dropdown
  useEffect(() => {
    if (isAdmin) {
      loadUsers();
    }
  }, [isAdmin]);

  // Redirect if not admin
  if (!isAdmin) {
    return (
      <div className="container-center">
        <div className="card card-center">
          <h1 className="title">Access Denied</h1>
          <p className="subtitle">You do not have administrator privileges.</p>
          <button onClick={() => onNavigate('home')} className="btn btn-primary">
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const loadUsers = async () => {
    try {
      const userSet = new Set();

      // Query all location tables for unique users
      const promises = locationTables.map(async (table) => {
        const { data } = await supabase
          .from(table)
          .select('user_name')
          .not('user_name', 'is', null);

        return data || [];
      });

      const results = await Promise.all(promises);

      results.flat().forEach(record => {
        if (record.user_name) {
          userSet.add(record.user_name);
        }
      });

      setUsers(Array.from(userSet).sort());
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const runReport = async () => {
    if (!startDate || !endDate) {
      alert('Please select both start and end dates.');
      return;
    }

    setLoading(true);
    setReportData([]);
    setChartData({ providers: {}, locations: {} });

    try {
      const allData = [];
      const providerCounts = {};
      const locationCounts = {};

      // Query all location tables
      const promises = LOCATIONS.map(async (loc) => {
        const { data, error } = await supabase
          .from(loc.tableName)
          .select('*')
          .eq('reason_for_change', 'Patient Use/Restock')
          .gte('created_at', startDate)
          .lte('created_at', endDate + 'T23:59:59')
          .order('created_at', { ascending: false });

        if (error) {
          console.error(`Error querying ${loc.tableName}:`, error);
          return [];
        }

        return (data || []).map(record => ({ ...record, locationName: loc.name }));
      });

      const results = await Promise.all(promises);
      const flatResults = results.flat();

      // Process data
      flatResults.forEach(record => {
        const userName = record.user_name || '';

        // Apply user filter if selected
        if (selectedUser && selectedUser !== userName) return;

        // Add to results
        allData.push({
          location: record.locationName || '',
          date: record.created_at ? new Date(record.created_at).toLocaleDateString() : '',
          time: record.created_at ? new Date(record.created_at).toLocaleTimeString() : '',
          runNumber: record.run_number || '',
          userName: userName
        });

        // Count for charts
        if (userName) {
          providerCounts[userName] = (providerCounts[userName] || 0) + 1;
        }
        if (record.locationName) {
          locationCounts[record.locationName] = (locationCounts[record.locationName] || 0) + 1;
        }
      });

      setReportData(allData);
      setChartData({ providers: providerCounts, locations: locationCounts });
    } catch (error) {
      console.error('Error running report:', error);
      alert('Error running report. Please check console for details.');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column) => {
    let direction = 'asc';
    if (sortConfig.key === column && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: column, direction });

    const sortedData = [...reportData].sort((a, b) => {
      if (a[column] < b[column]) return direction === 'asc' ? -1 : 1;
      if (a[column] > b[column]) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setReportData(sortedData);
  };

  const exportToCSV = () => {
    if (reportData.length === 0) {
      alert('No data to export.');
      return;
    }

    const headers = ['Location', 'Date', 'Time', 'Run #', 'User'];
    const csvData = [
      headers.join(','),
      ...reportData.map(row =>
        [row.location, row.date, row.time, row.runNumber, row.userName]
          .map(field => `"${field}"`)
          .join(',')
      )
    ].join('\n');

    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `provider_usage_${BRANDING.companyName.toLowerCase().replace(/\s+/g, '_')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getSortIcon = (column) => {
    if (sortConfig.key !== column) return '';
    return sortConfig.direction === 'asc' ? ' ^' : ' v';
  };

  const renderChart = (data, title) => {
    const entries = Object.entries(data);
    if (entries.length === 0) return null;

    const maxCount = Math.max(...entries.map(([, count]) => count));

    return (
      <div style={{ margin: '20px 0' }}>
        <h4 style={{ textAlign: 'center', marginBottom: '20px', color: 'var(--text-color)' }}>{title}</h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {entries.map(([name, count]) => (
            <div key={name} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                minWidth: '150px',
                fontSize: '14px',
                textAlign: 'right',
                color: 'var(--text-color)'
              }}>
                {name}:
              </div>
              <div style={{
                background: 'var(--primary-color)',
                height: '25px',
                width: `${(count / maxCount) * 300}px`,
                minWidth: '20px',
                borderRadius: '3px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '12px',
                fontWeight: '600'
              }}>
                {count}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="container">
      <HamburgerMenu
        isAdmin={isAdmin}
        onLogout={onLogout}
        onNavigate={onNavigate}
      />

      <div className="content">
        {/* Back Button */}
        <button
          onClick={() => onNavigate('admin-dashboard')}
          className="btn btn-secondary"
          style={{ marginBottom: '20px', padding: '10px 20px' }}
        >
          Back to Admin
        </button>

        {/* Header */}
        <div className="card" style={{
          textAlign: 'center',
          background: 'var(--primary-color)',
          color: 'white',
          marginBottom: '30px'
        }}>
          <h1 style={{ color: 'white', marginBottom: '10px' }}>
            Provider Usage Report
          </h1>
          <p style={{ color: 'white', margin: 0 }}>
            Track patient use and restock events by provider and location.
          </p>
        </div>

        {/* Filters */}
        <div className="card" style={{ marginBottom: '30px' }}>
          <h3 className="section-title">Report Filters</h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '20px',
            alignItems: 'end'
          }}>
            <div>
              <label className="form-label">Start Date</label>
              <input
                type="date"
                className="form-input"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>

            <div>
              <label className="form-label">End Date</label>
              <input
                type="date"
                className="form-input"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>

            <div>
              <label className="form-label">Filter by User</label>
              <select
                className="form-input"
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
              >
                <option value="">All Users</option>
                {users.map(user => (
                  <option key={user} value={user}>
                    {user}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <button
                className="btn btn-primary btn-full"
                onClick={runReport}
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Run Report'}
              </button>
            </div>

            <div>
              <button
                className="btn btn-secondary btn-full"
                onClick={exportToCSV}
                disabled={reportData.length === 0}
              >
                Export to CSV
              </button>
            </div>
          </div>
        </div>

        {/* Chart Controls */}
        {reportData.length > 0 && (
          <div className="card" style={{ marginBottom: '30px', textAlign: 'center' }}>
            <h3 className="section-title">Data Visualization</h3>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                className="btn btn-secondary"
                onClick={() => setShowProviderChart(!showProviderChart)}
              >
                {showProviderChart ? 'Hide' : 'Show'} Provider Chart
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setShowLocationChart(!showLocationChart)}
              >
                {showLocationChart ? 'Hide' : 'Show'} Location Chart
              </button>
            </div>

            {showProviderChart && renderChart(chartData.providers, 'Provider Usage')}
            {showLocationChart && renderChart(chartData.locations, 'Location Usage')}
          </div>
        )}

        {/* Loading Indicator */}
        {loading && (
          <div style={{ textAlign: 'center', margin: '20px 0' }}>
            <p>Loading report...</p>
          </div>
        )}

        {/* Results Table */}
        {reportData.length > 0 && (
          <div className="card">
            <h3 className="section-title">
              Report Results ({reportData.length} records)
            </h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                marginTop: '20px'
              }}>
                <thead>
                  <tr style={{ background: 'var(--background-color)' }}>
                    {[
                      { key: 'location', label: 'Location' },
                      { key: 'date', label: 'Date' },
                      { key: 'time', label: 'Time' },
                      { key: 'runNumber', label: 'Run #' },
                      { key: 'userName', label: 'User' }
                    ].map(column => (
                      <th
                        key={column.key}
                        onClick={() => handleSort(column.key)}
                        style={{
                          padding: '12px',
                          textAlign: 'left',
                          cursor: 'pointer',
                          borderBottom: '2px solid var(--primary-color)',
                          fontWeight: '600',
                          color: 'var(--text-color)'
                        }}
                      >
                        {column.label}{getSortIcon(column.key)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {reportData.map((row, index) => (
                    <tr key={index} style={{
                      borderBottom: '1px solid var(--border-color)',
                      backgroundColor: index % 2 === 0 ? 'white' : 'var(--background-color)'
                    }}>
                      <td style={{ padding: '12px', color: 'var(--text-color)' }}>{row.location}</td>
                      <td style={{ padding: '12px', color: 'var(--text-color)' }}>{row.date}</td>
                      <td style={{ padding: '12px', color: 'var(--text-color)' }}>{row.time}</td>
                      <td style={{ padding: '12px', color: 'var(--text-color)' }}>{row.runNumber}</td>
                      <td style={{ padding: '12px', color: 'var(--text-color)' }}>{row.userName}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* No Results Message */}
        {!loading && reportData.length === 0 && startDate && endDate && (
          <div className="card" style={{ textAlign: 'center' }}>
            <p style={{ color: 'var(--text-muted)', fontSize: '16px' }}>
              No patient use/restock records found for the selected criteria.
            </p>
          </div>
        )}
      </div>

      <footer className="footer">
        <p className="text-small">
          Provider Usage Report - {BRANDING.copyright}
        </p>
      </footer>
    </div>
  );
}

export default ProviderUsageReport;
